import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Countdown from 'react-countdown';

import ExtensionSubMenu from '@/components/nav/ExtensionSubMenu';
import Popup4868 from '@/components/ui/4868Popup';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';
import { getLastDateOfTheMonth } from '@/utils/utils';

const Form4868 = () => {
  const [referralUrl, SetReferralUrl] = useState('form4868');
  const layoutWrapperContents: LayoutWrapperContents = {
    Heading: `Need more time to file your individual tax return?`,
    SubHeading: 'E-file Form 4868 & get a 6-month extension.',
    CtaText: 'E-file Form 4868 Now',
    CtaSubHeading: `Takes 3 steps and less than 5 minutes`,
    CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form4868sticky`,
    isActive: true,
  };
  useEffect(() => {
    const isIRSPageReferral = document.referrer === 'https://www.irs.gov/';
    if (isIRSPageReferral) SetReferralUrl('form4868irs');
  }, []);
  const [lastDayTaxSeason, setLastDayTaxSeason] = useState(new Date());

  useEffect(() => {
    setLastDayTaxSeason(getLastDateOfTheMonth(new Date().getFullYear(), 4, 15));
    setInterval(() => {
      setLastDayTaxSeason(
        getLastDateOfTheMonth(new Date().getFullYear(), 4, 15)
      );
    }, 1000); // Updates every second (adjust if needed)
  }, []);

  // Random component
  const Completionist = () => <span />;

  // Renderer callback with condition
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      // Render a completed state
      return <Completionist />;
    }
    // Render a countdown
    return (
      <Stack
        direction="row"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        alignItems="center"
        spacing={1}
        sx={{
          '& .timer-count': {
            border: '1px solid #848484',
            p: 1.5,
            textAlign: 'center',
            minWidth: { xs: 55, sm: 70, md: 70 },
            position: 'relative',
            borderRadius: 2,
            boxShadow: ' 0 0 20px rgba(0,0,0,.12)',
          },
          '& .timer-number': {
            fontSize: { xs: 18, sm: 24 },
            fontWeight: 900,
          },
          '& .colon': {
            fontSize: { xs: 18, sm: 24 },
            px: { xs: 0.2, sm: 0.5 },
            py: 0,
            mt: { xs: '-8px !important', sm: '-14px !important' },
          },
          '& .timer-label': {
            background: '#000',
            borderTop: '1px solid #848484',
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 500,
            mt: 0,
            mx: -1.5,
            mb: -1.5,
            py: 0.2,
            px: { xs: 0.5, sm: 1, lg: 0.5 },
            borderRadius: '0px 0px 8px 8px',
          },
        }}
      >
        <Box className="timer-count">
          <Box className="timer-number">
            {days?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Day</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {hours?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Hours</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {minutes?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Minutes</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {seconds?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Seconds</Box>
        </Box>
      </Stack>
    );
  };
  return (
    <AppLayout
      LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          titleTemplate="E-File Form 4868 Online | Individual Tax Extension"
          description="File Form 4868 online with TaxZerone to get an automatic 6-month extension for your individual tax return. Fast and easy tax extension filing in minutes."
          keywords="form 4868, irs form 4868, 4868 form, e file form 4868 tax extension, form 4868 online, form 4868 instructions, instructions for form 4868, form 4868 extension, tax form 4868, IRS authorized provider"
          canonical="tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
          ogShareImg="assets/images/extension-share-img.png"
        />
      }
    >
      <ExtensionSubMenu />
      <Popup4868 />
      <Container
        sx={{
          position: 'relative',
          mt: 2,
          mb: 0,
        }}
        maxWidth="lg"
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #111d13, #709775)',
            p: 1.5,
            px: 2.5,
            color: '#fff',
          }}
          className="ribbon"
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box>
              <Typography
                component="div"
                variant="body1"
                sx={{
                  textAlign: { xs: 'center', sm: 'left' },
                  fontSize: { xs: 15, sm: 17, md: 21 },
                  color: '#e1e1e1',
                  lineHeight: 1.3,
                }}
              >
                <strong>📢 Attention Taxpayers! </strong> E-file{' '}
                <b style={{ color: '#fff', fontWeight: 600 }}>Form 4868</b> now
                today and get up to 6 additional months to file your return.
              </Typography>
            </Box>
            <Box>
              {/* <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: { xs: 1, sm: 1 },
                  lineHeight: 1.3,
                }}
              >
                Time Left Until the Deadline:{' '}
                <span style={{ fontWeight: 600, color: '#fff' }}>
                  April 15, 2026
                </span>
              </Typography> */}
              <Countdown date={lastDayTaxSeason} renderer={renderer} />
            </Box>

            <Box>
              <Button
                variant="contained"
                size="small"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form4868`}
                sx={{
                  backgroundColor: '#333',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                  },
                  px: 2,
                  color: '#fff',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: 18,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                E-file Now!
              </Button>
            </Box>
          </Stack>
        </Box>
      </Container>

      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                E-file{' '}
                <span className="highlight-container">
                  <span className="highlight">Form 4868</span>
                </span>{' '}
                for 2025 Tax Year & Get a
                <Hidden mdUp smDown>
                  <br />
                </Hidden>{' '}
                6-Month IRS Extension
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                Need more time to file your personal income tax? E-file IRS Form
                4868 by April 15 and get an automatic 6-month extension for your
                Form 1040 with TaxZerone.
              </Typography>
              <Stack
                direction="row"
                spacing={6}
                justifyContent={{ xs: 'center', md: 'flex-start' }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                  >
                    Start{' '}
                    <Box
                      component="span"
                      sx={{ ml: 1, color: '#0bf25f', fontWeight: 700 }}
                    >
                      E-Filing Now
                    </Box>
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontSize: { xs: 11, sm: 14 } }}
                  >
                    Takes 3 steps and less than 5 minutes
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '22px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '22px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/4868-landing-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/4868-landing-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/4868-landing-img.png"
                  alt="E-file Form 4868 for 2025 Tax Year & Get a 6-Month IRS Extension"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/4868-landing-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/4868-landing-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/4868-landing-img-sm.png"
                  alt="E-file Form 4868 for 2025 Tax Year & Get a 6-Month IRS Extension "
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
              fontWeight: 600,
              mb: 3,
              textAlign: 'center',
            }}
          >
            Automatic Tax Filing Extensions for Individual Taxpayers
          </Typography>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
          >
            <Grid size={{ md: 6, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  minHeight: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  U.S. Citizens and Resident Aliens
                </Typography>
                <Typography variant="body1">
                  The automatic extension applies to individuals required to
                  file any of the following returns:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form 1040 –</span> U.S.
                    Individual Income Tax Return
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form 1040-SR –</span> Tax
                    return for taxpayers aged 65 or older
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form 1040-NR –</span>{' '}
                    U.S. Nonresident Alien Income Tax Return
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form 1040-SS –</span>{' '}
                    U.S. Self-Employment Tax Return
                  </li>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ md: 6, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: { md: 6, sm: 10.2, xs: 2 },
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  minHeight: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  Taxpayers Living Abroad
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Individuals residing outside the United States on the
                    regular due date.
                  </li>
                  <li>
                    They automatically receive a{' '}
                    <span style={{ fontWeight: 600 }}>2-month extension</span>{' '}
                    (to June 15), but should file{' '}
                    <span style={{ fontWeight: 600 }}> Form 4868</span> if
                    additional time (until October 15) is needed.
                  </li>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ md: 6, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  minHeight: '100%',
                  justifyItems: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  Members of the Military
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Taxpayers serving in combat zones may receive an automatic
                    extension equal to their service period, plus 180 days
                    (about 6 months) after leaving the combat zone, along with
                    any remaining time before the original filing deadline.
                  </li>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ md: 6, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  minHeight: '100%',
                  justifyItems: 'center',
                  alignItems: 'center',
                  py: { md: 7.3, sm: 8.9, xs: 2 },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  Self-Employed Individuals
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Taxpayers with business income reported on Schedule C who
                    need more time to gather financial information.
                  </li>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ md: 6, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  minHeight: '100%',
                  justifyItems: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  Taxpayers Waiting for Tax Documents
                </Typography>
                <Typography variant="body1">
                  Individuals who have not yet received essential forms such as:
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Form W-2</li>
                  <li>Form 1099 </li>
                  <li>Schedule K-1 </li>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ md: 6, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  minHeight: '100%',
                  justifyItems: 'center',
                  alignItems: 'center',
                  py: 7.3,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  Taxpayers with Complex Returns
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Those with multiple income sources, investments, foreign
                    assets, or significant deductions and credits.{' '}
                  </li>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <Box
                sx={{
                  maxWidth: 'md',
                  wdith: '100%',
                  p: 3,
                  mx: 'auto',
                  borderRadius: 3,
                  backgroundColor: 'rgb(255, 244, 229)',
                  borderLeft: '3px solid rgb(102, 60, 0)',
                }}
              >
                <b>⚠️ Important Notes </b>
                <br />{' '}
                <span style={{ fontWeight: 600 }}>
                  {' '}
                  Extension to File, Not to Pay:
                </span>{' '}
                Form 4868 grants more time to file your return, not more time to
                pay any taxes owed. You should estimate and pay any tax
                liability by the original due date
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                How to File Form 4868 Online in 3 Simple Step?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Follow the steps below to file irs form 4868 online for 2025 tax
                year with TaxZerone.
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source
                  srcSet="/assets/images/4868-steps-img.webp"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <source
                  srcSet="/assets/images/4868-steps-img.png"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <img
                  src="/assets/images/4868-steps-img.png"
                  alt="How to E-File IRS Form 4868 in 3 simple steps with TaxZerone?"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/step-1-icon.webp"
                          width={39}
                          height={39}
                        />
                        <source
                          srcSet="/assets/images/step-1-icon.png"
                          width={39}
                          height={39}
                        />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Enter form information"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter form information
                    </Typography>
                    <Typography variant="body1">
                      Select the tax year and enter personal information such as
                      name, address, and SSN. Calculate the balance due by
                      providing tax liability and payments made.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Preview the return"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Preview the return
                    </Typography>
                    <Typography variant="body1">
                      Review whether the information entered is correct and make
                      changes if required. Provide bank account information if
                      you have a balance due.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="Transmit the return to the IRS"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Transmit the return to the IRS
                    </Typography>
                    <Typography variant="body1">
                      Securely transmit your return to the IRS and wait for an
                      email notification from us on the status of your return.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Timeline>

              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                >
                  E-File Form 4868 Now
                </Button>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why Choose TaxZerone?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Explore our powerful feature that makes your Form 4868 IRS
                filing easy.
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/fast-filing-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/fast-filing-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/fast-filing-icon.png"
                      alt="Get IRS Confirmation Fast "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Get IRS Confirmation Fast
                </Typography>
                <Typography variant="body1">
                  Receive quick acknowledgment from the IRS after filing. Stay
                  informed with real-time status updates so you know your
                  extension is successfully submitted.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/guided-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/guided-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/guided-icon.png"
                      alt="No Tax Knowledge Required "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  No Tax Knowledge Required
                </Typography>
                <Typography variant="body1">
                  You don’t need to be a tax expert. Our guided filing process
                  makes it easy to complete your extension accurately.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/secure-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/secure-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/secure-icon.png"
                      alt="100% Safe & Encrypted  "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  100% Safe & Encrypted
                </Typography>
                <Typography variant="body1">
                  Your data is protected with advanced encryption and secure
                  systems. TaxZerone ensures your personal and financial
                  information stays completely safe.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/mobile-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/mobile-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/mobile-icon.png"
                      alt="Mobile App "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Mobile App
                </Typography>
                <Typography variant="body1">
                  You can download our Form 4868 mobile app and file your
                  individual tax extension from anywhere in the world.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/lowest-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/lowest-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/lowest-icon.png"
                      alt="Affordable Pricing "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Affordable Pricing
                </Typography>
                <Typography variant="body1">
                  We offer the affordable price in the industry without any
                  hidden charges and making efiling easy for everyone
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/support-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/support-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="Friendly Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Friendly Support
                </Typography>
                <Typography variant="body1">
                  Our expert team is here to assist you with your filing queries
                  via Phone, email and live chat.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          py: { xs: 3, sm: 5, md: 6, lg: 8 },
          mx: '0 !important',
          width: '100% !important',
          backgroundSize: { xs: '25% auto', sm: '15% auto', md: '10% auto' },
          // position: { xs: 'static', md: 'sticky' },
          // top: 64,
          // zIndex: 99,
        }}
        spacing={2}
        m={0}
        //  className="wave-bg"
      >
        <Container maxWidth="xl" sx={{ mb: 0 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
              fontWeight: 600,

              mb: { xs: 3, md: 1 },
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            File Personal Tax Extensions at Scale for Tax Pros
          </Typography>
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid size={{ md: 6, xs: 12 }} textAlign="right">
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/personal-tax-img.webp" />
                  <source srcSet="/assets/images/personal-tax-img.png" />
                  <img
                    src="/assets/images/personal-tax-img.png"
                    alt="File Personal Tax Extensions at Scale for Tax Pros"
                    className="img-responsive-banner"
                    width={600}
                    height={550}
                    style={{ borderRadius: 21 }}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{}}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    textAlign: { xs: 'center', md: 'left' },
                    // lineHeight: 1.5,
                    '& .sub-title': {
                      fontSize: { md: 26, sm: 22, xs: 18 },
                    },
                  }}
                >
                  File Form 4868 Extension for all your Clients at once!
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  Skip the manual work! Upload a single excel and we will handle
                  every extension request in one go!
                </Typography>
                <Stack direction="column" spacing={2}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                      backgroundColor: '#fff',
                      display: 'grid',
                      gridTemplateColumns: 'auto auto',
                      justifyContent: 'left',
                      alignItems: 'center',
                      gap: 1,
                      maxWidth: 600,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 2,
                        backgroundColor: '#efefef',
                        width: 80,
                        height: 80,
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        display: 'flex',
                        m: 'auto',
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/template-icon.webp"
                          width={50}
                          height={50}
                        />
                        <source
                          srcSet="/assets/images/template-icon.png"
                          width={50}
                          height={50}
                        />
                        <img
                          src="/assets/images/template-icon.png"
                          alt="Download our template"
                          loading="lazy"
                          width={50}
                          height={50}
                        />
                      </picture>
                    </Box>
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Download our template
                      </Typography>
                      <Typography variant="body1">
                        Get our preformatted Form 4868 template – just fill in
                        your client details.
                      </Typography>
                    </Box>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      minHeight: '100%',
                      backgroundColor: '#fff',
                      display: 'grid',
                      gridTemplateColumns: 'auto auto',
                      justifyContent: 'left',
                      alignItems: 'center',
                      gap: 1,
                      maxWidth: 600,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 2,
                        backgroundColor: '#efefef',
                        width: 80,
                        height: 80,
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        display: 'flex',
                        m: 'auto',
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/tem-upload-icon.webp"
                          width={50}
                          height={50}
                        />
                        <source
                          srcSet="/assets/images/tem-upload-icon.png"
                          width={50}
                          height={50}
                        />
                        <img
                          src="/assets/images/tem-upload-icon.png"
                          alt="Upload your File"
                          loading="lazy"
                          width={50}
                          height={50}
                        />
                      </picture>
                    </Box>
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Upload your File
                      </Typography>
                      <Typography variant="body1">
                        Upload your completed Excel File and we will instantly
                        validate every record
                      </Typography>
                    </Box>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      minHeight: '100%',
                      backgroundColor: '#fff',
                      display: 'grid',
                      gridTemplateColumns: 'auto auto',
                      justifyContent: 'left',
                      alignItems: 'center',
                      gap: 1,
                      maxWidth: 600,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 2,
                        backgroundColor: '#efefef',
                        width: 80,
                        height: 80,
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        display: 'flex',
                        m: 'auto',
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/tem-transmit-icon.webp"
                          width={50}
                          height={50}
                        />
                        <source
                          srcSet="/assets/images/tem-transmit-icon.png"
                          width={50}
                          height={50}
                        />
                        <img
                          src="/assets/images/tem-transmit-icon.png"
                          alt="Review and Transmit"
                          loading="lazy"
                          width={50}
                          height={50}
                        />
                      </picture>
                    </Box>
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Review and Transmit
                      </Typography>
                      <Typography variant="body1">
                        Confirm your records and transmit them in a single click
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 }, mb: 5, mt: 5 }}>
          <Grid container spacing={2}>
            <Grid
              size={{ md: 12, sm: 12, xs: 12 }}
              justifyContent="center"
              sx={{ mb: 5, mt: 4 }}
            >
              <Typography
                variant="h2"
                //  color="white"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  mt: 2,
                  textAlign: 'center',
                }}
              >
                Due Date to File your IRS Extension 4868
              </Typography>
              <Box component="div" sx={{ mb: 4, maxWidth: 700, m: 'auto' }}>
                <TableContainer
                  sx={{ border: '1px solid #828282', borderRadius: 2 }}
                >
                  <Table sx={{}} aria-label="Due Date Chart">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#ccc' }}>
                        <TableCell
                          sx={{
                            color: '#444',
                            fontWeight: 600,
                            width: 400,
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            fontSize: 17,
                          }}
                        >
                          Forms Covered
                        </TableCell>

                        <TableCell
                          sx={{
                            color: '#444',
                            fontWeight: 600,
                            width: 200,
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            fontSize: 17,
                          }}
                        >
                          Original Due Date
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#444',
                            fontWeight: 600,
                            width: 200,
                            fontSize: 17,
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                          }}
                        >
                          Extended Due Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            backgroundColor: '#fff',
                            textAlign: 'left',
                            fontSize: 16,
                          }}
                        >
                          <span style={{ fontWeight: 600 }}> Form 1040</span>{' '}
                          (U.S. Individual Income Tax Return)
                          <br />
                          <span style={{ fontWeight: 600 }}> Form 1040-SR</span>
                          (U.S. Tax Return for Seniors) <br />
                          <span style={{ fontWeight: 600 }}> Form 1040-SS</span>
                          (U.S. Self-Employment Tax Return)
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          April 15, 2026
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          October 15, 2026
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',

                            backgroundColor: '#fff',
                            textAlign: 'left',
                            fontSize: 16,
                          }}
                        >
                          <span style={{ fontWeight: 600 }}> Form 1040-NR</span>{' '}
                          (U.S. Nonresident Alien Income Tax Return)
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',

                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          June 15,2026
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#fff',

                            fontSize: 16,
                          }}
                        >
                          October 15, 2026
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mt: 6, mb: 8 }}>
          <Grid container spacing={2} m={0} className="MuiGrid-root-width">
            <Grid
              size={{ xs: 12, sm: 12, md: 12 }}
              sx={{ justifyContent: 'center' }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Our Pricing
              </Typography>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  background:
                    'linear-gradient(99deg,rgba(246, 235, 255, 0.23) 0%, rgba(176, 219, 255, 0.19) 100%)',
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  justifyContent: 'center',

                  display: 'flex',
                  flexDirection: 'column',

                  minHeight: '100%',
                  border: '2px solid rgb(33, 62, 153)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Form 4868
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Extension to File Individual Income Tax Return
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { md: 18 } }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { md: 34, sm: 26, xs: 22 },
                      fontWeight: 700,
                      color: '#333',
                    }}
                  >
                    {' '}
                    $11.99
                  </Box>
                  /return
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  background:
                    'linear-gradient(99deg,rgba(181, 250, 255, 0.23) 0%, rgba(133, 200, 255, 0.19) 100%)',
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  justifyContent: 'center',

                  display: 'flex',
                  flexDirection: 'column',

                  minHeight: '100%',
                  border: '2px solid rgb(33, 62, 153)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  E-File 4868 and 7004
                </Typography>

                <Typography variant="body2" sx={{ fontSize: { md: 18 } }}>
                  <span style={{ textDecoration: 'line-through' }}>
                    {' '}
                    $29.98
                  </span>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { md: 34, sm: 26, xs: 22 },
                      fontWeight: 700,
                      color: 'rgb(33, 62, 153)',
                    }}
                  >
                    {' '}
                    $24.99
                  </Box>
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  background:
                    'linear-gradient(99deg,rgba(181, 250, 255, 0.23) 0%, rgba(133, 200, 255, 0.19) 100%)',
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  justifyContent: 'center',

                  display: 'flex',
                  flexDirection: 'column',

                  minHeight: '100%',
                  border: '2px solid rgb(33, 62, 153)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  E-File 4868 and 8868
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: {
                      md: 18,
                      alignItems: 'center',
                      justifyContent: 'center',
                      display: 'flex',
                      pt: 2,
                      gap: 1,
                    },
                  }}
                >
                  {' '}
                  <span style={{ textDecoration: 'line-through' }}>
                    {' '}
                    $26.98
                  </span>{' '}
                  &nbsp;
                  <Box
                    component="span"
                    sx={{
                      fontSize: { md: 34, sm: 26, xs: 22 },
                      fontWeight: 700,
                      color: 'rgb(33, 62, 153)',
                    }}
                  >
                    {' '}
                    $21.99
                  </Box>
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                >
                  Get Extension Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mt: 0, mb: 7 }}>
          <Grid container spacing={2}>
            <Grid size={{ md: 6, xs: 12 }} sx={{ mt: 4 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
                className="image-container"
              >
                <picture>
                  <source srcSet="/assets/images/1120s-penalties-late-img.webp" />
                  <source srcSet="/assets/images/1120s-penalties-late-img.png" />
                  <img
                    src="/assets/images/1120s-penalties-late-img.png"
                    alt="Penalties for Late or Inaccurate Filing"
                    className="img-responsive"
                    loading="lazy"
                    width={450}
                    height={450}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                textAlign: { md: 'left', sm: 'left' },
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                m: 0,
              }}
            >
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 3,

                    textAlign: 'left',
                  }}
                >
                  IRS Form 4868 Penalties for Late Filing and Late Payment
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Late Filing Penalty{' '}
                    </span>
                    The penalty is usually 5% to the maximum of 25%. If your
                    return is more than 60 days late, the minimum penalty is
                    $525 or the balance due.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Late Payment Penalty
                    </span>{' '}
                    The late payment penalty is usually ½ of 1% of any tax
                    (other than estimated tax) not paid by the due date of your
                    return, which is April 15, 2026. The maximum penalty is 25%.
                  </li>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={7}
            mb={7}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={12}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'center',
                  md: 'center',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Why TaxZerone is the #1 Choice for Individual{' '}
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>{' '}
                Tax Extensions filing?
              </Typography>

              <Box component="div" sx={{ mb: 4, maxWidth: 900, m: 'auto' }}>
                <TableContainer sx={{ border: '1px solid #828282' }}>
                  <Table sx={{}} aria-label="Due Date Chart">
                    <TableHead>
                      <TableRow
                        sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                      >
                        <TableCell
                          sx={{
                            color: '#444',
                            width: 300,
                            fontWeight: 600,
                            fontSize: { md: 18, sm: 16, xs: 16 },
                          }}
                        >
                          Feature
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#444',
                            width: 300,
                            fontWeight: 600,
                            fontSize: { md: 18, sm: 16, xs: 16 },
                          }}
                        >
                          TaxZerone
                        </TableCell>

                        <TableCell
                          sx={{
                            color: '#444',
                            width: 300,
                            fontWeight: 600,
                            fontSize: { md: 18, sm: 16, xs: 16 },
                          }}
                        >
                          Other E-file Providers
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#444',
                            width: 300,
                            fontWeight: 600,
                            fontSize: { md: 18, sm: 16, xs: 16 },
                          }}
                        >
                          Manual Filing (Paper)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          IRS-Authorized{' '}
                        </TableCell>
                        <TableCell>✅ Yes </TableCell>
                        <TableCell>✅ Yes </TableCell>
                        <TableCell>❌ No </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Filing Time
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                            verticalAlign: 'center',
                          }}
                        >
                          ✅ File in minutes
                        </TableCell>
                        <TableCell>✅10 to 15 minutes </TableCell>
                        <TableCell>
                          Time-consuming (print, fill, mail){' '}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Error Validation
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                          }}
                        >
                          ✅ Built-in IRS Form validations
                        </TableCell>
                        <TableCell>❌ Limited validations </TableCell>
                        <TableCell>
                          No validation (higher error risk){' '}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Accuracy
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                          }}
                        >
                          High (system checks)
                        </TableCell>
                        <TableCell>Moderate </TableCell>
                        <TableCell>Prone to manual errors </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Processing Speed
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                          }}
                        >
                          ✅ Instant
                        </TableCell>
                        <TableCell>Standard processing </TableCell>
                        <TableCell>Delayed (mail + IRS processing) </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Confirmation
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                          }}
                        >
                          Instant IRS acceptance notification
                        </TableCell>
                        <TableCell>1-2 days </TableCell>
                        <TableCell>No immediate confirmation </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Retransmission
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                          }}
                        >
                          ✅Free
                        </TableCell>
                        <TableCell>❌May charge extra </TableCell>
                        <TableCell>❌Refile Form 4868 </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Pricing
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                            fontSize: 20,
                          }}
                        >
                          <span style={{ fontWeight: 700 }}> $11.99</span>
                          /return
                        </TableCell>
                        <TableCell>Higher / add-on fees </TableCell>
                        <TableCell>
                          Low cost but indirect costs (time, postage){' '}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Bulk Upload
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            justifyContent: 'center',
                          }}
                        >
                          Supported
                        </TableCell>
                        <TableCell>Limited</TableCell>
                        <TableCell>Not possible </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell
                          sx={{
                            fontSize: { md: 16, sm: 16, xs: 16 },
                            fontWeight: 600,
                          }}
                        >
                          Support
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          ✅{' '}
                          <Box>
                            Human support via Phone (English & Spanish), live
                            chat and email{' '}
                          </Box>
                        </TableCell>
                        <TableCell>❌Limited support</TableCell>
                        <TableCell>❌No Support </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, mt: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> TaxZerone offers the perfect balance of
                  compliance, convenience, and cost. Don’t settle for outdated
                  or overpriced alternatives.
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          background: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
                justifyContent={{ xs: 'center', md: 'center' }}
              >
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-1"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-2"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-3"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-4"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-5"
                />
              </Stack>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 1 },
                }}
              >
                See How Filing Form 4868 Became Easier for Individuals
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  mb: { xs: 1, sm: 3 },
                }}
              >
                See why taxpayers trust TaxZerone for fast, simple, and reliable
                tax extension filing.
              </Typography>

              <Grid
                container
                spacing={2}
                m={0}
                justifyContent="center"
                direction="row"
                className="MuiGrid-root-width"
                sx={{ mb: 3 }}
              >
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        Everything was straightforward. I didn’t face any
                        errors, and my filing was accepted instantly.
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      - David Martinez
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        I filed my Form 4868 in just a few minutes. The steps
                        were clear and simple to follow
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      - Orion Bennett
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        I handle multiple clients, and this made filing all
                        their extensions simple.
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      - Marlowe Quinn
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 }, mb: 3 }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                //  color="white"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Learn How to File IRS Extension Form 4868 Through Videos
              </Typography>
            </Grid>
            <Grid
              size={12}
              sx={{
                textAlign: 'center',
              }}
            >
              {' '}
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'center' },
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                  mt: 3,
                }}
              >
                <Box
                  component="iframe"
                  title=" What is form 4868 and how to E-file the form"
                  src="https://www.youtube.com/embed/VFNXuCJHEoY?rel=0"
                  sx={{
                    width: { xs: '100%', sm: 610 },
                    height: { xs: '100%', sm: 353 },
                    border: '10px solid #333 !important',
                    borderRadius: 5,
                  }}
                  allowFullScreen={true}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Ready to extend your Form 1040 filing deadline?
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
          }}
        >
          E-file Form 4868 online and get an automatic 6-month extension in
          minutes.
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
          >
            Get an&nbsp;<b>Extension Today</b>
          </Button>
          <Typography
            variant="body2"
            sx={{ mt: 1, mb: { xs: 0, md: 0 } }}
            color="#dfeae1"
          >
            Simple, Secure & Affordable
          </Typography>
        </Box>
      </Box>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid size={12}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                mt: 1,
                textAlign: 'center',
              }}
            >
              Helpful Resources section
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link
              href="/resources/tax-extension/form-4868-instructions/"
              sx={{ textDecoration: 'none' }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 0,
                  py: 0,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  backgroundColor: '#f0f2f9',
                  transition: 'all 0.3s ease 0s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    mb: 0,
                    borderTopRightRadius: 12,
                    borderTopLeftRadius: 12,
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/f941-instruction-img.webp" />
                    <source srcSet="/assets/images/f941-instruction-img.png" />
                    <img
                      src="/assets/images/f941-instruction-img.png"
                      alt="Form 4868 Instructions"
                      className="partner-card"
                      loading="lazy"
                      width={361}
                      height={292}
                      style={{
                        borderTopRightRadius: 12,
                        borderTopLeftRadius: 12,
                      }}
                    />
                  </picture>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      my: 1,
                      textAlign: 'center',
                    }}
                  >
                    Form 4868 Instructions
                  </Typography>
                  <Typography variant="body1">
                    Read the TaxZerone’s step-by-step instructions to file your
                    extension accurately and on time.
                  </Typography>
                </Box>
              </Paper>
            </Link>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link
              href="/resources/tax-extension/form-4868-due-dates/"
              sx={{ textDecoration: 'none' }}
            >
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  backgroundColor: '#f0f2f9',
                  transition: 'all 0.3s ease 0s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    mb: 0,
                    borderTopRightRadius: 12,
                    borderTopLeftRadius: 12,
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/f941-due-date-img.webp" />
                    <source srcSet="/assets/images/f941-due-date-img.png" />
                    <img
                      src="/assets/images/f941-due-date-img.png"
                      alt="Form 4868 Due Date"
                      className="partner-card"
                      loading="lazy"
                      width={361}
                      height={292}
                      style={{
                        borderTopRightRadius: 12,
                        borderTopLeftRadius: 12,
                      }}
                    />
                  </picture>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      my: 1,
                      textAlign: 'center',
                    }}
                  >
                    Form 4868 Due Date
                  </Typography>
                  <Typography variant="body1">
                    Key deadlines explained to help you file 4868 before the
                    deadline with TaxZerone.
                  </Typography>
                </Box>
              </Paper>
            </Link>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link
              href="/resources/tax-extension/form-4868-mailing-addresses-and-filing-guide/"
              sx={{ textDecoration: 'none' }}
            >
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',

                  backgroundColor: '#f0f2f9',
                  transition: 'all 0.3s ease 0s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    mb: 0,
                    borderTopRightRadius: 12,
                    borderTopLeftRadius: 12,
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/f941-mail-add-img.webp" />
                    <source srcSet="/assets/images/f941-mail-add-img.png" />
                    <img
                      src="/assets/images/f941-due-date-img.png"
                      alt="Form 4868 Mailing Address"
                      className="partner-card"
                      loading="lazy"
                      width={361}
                      height={292}
                      style={{
                        borderTopRightRadius: 12,
                        borderTopLeftRadius: 12,
                      }}
                    />
                  </picture>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      my: 1,
                      textAlign: 'center',
                    }}
                  >
                    Form 4868 Mailing Address
                  </Typography>
                  <Typography variant="body1">
                    Use the correct 4868 mailing address based on your state of
                    residence.
                  </Typography>
                </Box>
              </Paper>
            </Link>
          </Grid>
          <Grid
            size={12}
            sx={{ borderTop: ' 1px solid rgba(143, 156, 169, 0.24)', mt: 8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                mt: 3,
                textAlign: 'center',
              }}
            >
              Recent Blogs
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link
              href="https://blog.taxzerone.com/posts/do-you-need-extra-time-to-file-your-individual-income-tax-extend-your-deadline-with-form-4868/"
              target="_blank"
              sx={{ textDecoration: 'none' }}
            >
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    border: '6px solid #42453f !important',
                    borderRadius: 3,
                    maxHeight: { xs: 183, sm: 134, md: 190, lg: 190 },
                    mb: 2,
                    '& img': {
                      transform: 'scale(1)',
                      transition: 'all .3s ease',
                      //  objectFit: 'cover',
                      width: '100%',
                      height: 'auto',
                    },
                    '& img:hover': { transform: 'scale(1.11)' },
                  }}
                >
                  <img
                    src="https://blog.taxzerone.com/wp-content/uploads/form-4868-extension.png"
                    alt="Do you need extra time to file your individual income tax (Form 1040)? Extend your deadline by filing Form 4868."
                    width={394}
                    height="100%"
                  />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body1">
                    {' '}
                    Do you need extra time to file your individual income tax
                    (Form 1040)? Extend your deadline by filing Form 4868.
                  </Typography>
                </Box>
              </Paper>
            </Link>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link
              href="https://blog.taxzerone.com/posts/form-4868-everything-you-need-to-know-about-the-income-tax-extension-form/"
              sx={{ textDecoration: 'none' }}
              target="_blank"
            >
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    border: '6px solid #42453f !important',
                    borderRadius: 3,
                    maxHeight: { xs: 183, sm: 134, md: 190, lg: 190 },
                    mb: 2,
                    '& img': {
                      transform: 'scale(1)',
                      transition: 'all .3s ease',
                      //  objectFit: 'cover',
                      width: '100%',
                      height: 'auto',
                    },
                    '& img:hover': { transform: 'scale(1.11)' },
                  }}
                >
                  <img
                    src="https://blog.taxzerone.com/wp-content/uploads/irs-form-4868.jpg"
                    alt="Form 4868: Everything you need to know about the income tax extension form "
                    width={394}
                    height="100%"
                  />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body1">
                    {' '}
                    Form 4868: Everything you need to know about the income tax
                    extension form
                  </Typography>
                </Box>
              </Paper>
            </Link>
          </Grid>

          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            sx={{ borderTop: ' 1px solid rgba(143, 156, 169, 0.24)', mt: 8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              FAQs on Form 4868
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is a 4868 tax form?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    <Link
                      href="/resources/tax-extension/form-4868-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 4868
                    </Link>{' '}
                    is filed by individual taxpayers to get an extension to file
                    income tax, that is, Forms 1040, 1040-SR, 1040-NR, or
                    1040-SS. By filing Form 4868, taxpayers can get up to 6
                    months of extension.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. When is the deadline to file Form 4868?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      For the 2025 tax year, you will need to e-file irs 4868
                      Form by <strong>April 15, 2026</strong>. Make sure to file
                      the extension form using TaxZerone within the due date to
                      avoid penalties.
                    </Typography>
                    <Typography>
                      Filing online irs form 4868 will extend the filing
                      deadline to <strong>October 15, 2026</strong>.
                    </Typography>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        mt: 1,

                        textAlign: 'left',
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      <b>Note:</b> For additional information, please refer to
                      our{' '}
                      <Link
                        href="/resources/tax-extension/form-4868-due-dates/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 4868 due date
                      </Link>{' '}
                      page.
                    </Alert>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Who can file Form 4868?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Any taxpayer who wants more time to file federal income tax
                    returns with the IRS can file Form 4868. The IRS
                    doesn&apos;t require you to provide any reason for filing an
                    extension.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. What information do I need to file irs online Form 4868?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography>
                      To file irs 4868 online form, you will need to have the
                      following information:
                    </Typography>
                    <List sx={{ pb: 0 }}>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>
                          Personal information such as Name, Address, and SSN
                        </ListItemText>
                      </ListItem>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>
                          Total tax liability for 2025
                        </ListItemText>
                      </ListItem>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>Tax payments made in 2025</ListItemText>
                      </ListItem>
                    </List>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  borderWidth: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover, &:focus, &:active': { borderWidth: 2 },
                }}
                href="/faqs/form-4868/"
              >
                More FAQs
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form4868;
